/**
 * 主题 token 注册表：新增 token 只需在此登记，并同步 types.ts 的 IThemeColors。
 * CSS 变量、Uno 类、校验、缓存、iframe 协议、Vant 映射均由这里派生。
 * 本目录（core/）为纯函数层：禁止依赖 DOM / Vue / `@` 别名，uno.config 在 Node 中加载它。
 */
import type { ThemeFontFamilyType, ThemeRadiusKeyType } from '../types';

export interface IThemeColorToken {
  /** 扁平 token 名：CSS 变量后缀与 Uno 类名后缀均以此为口径 */
  key: string;
  /** 在嵌套 colors 中的路径 */
  path: readonly string[];
  /** channels：值为「r g b」，类名支持 /60 透明度后缀；color：完整 CSS 颜色（自带透明度） */
  format: 'channels' | 'color';
}

export const THEME_COLOR_TOKENS = [
  { key: 'primary', path: ['primary'], format: 'channels' },
  { key: 'text', path: ['text', 'DEFAULT'], format: 'channels' },
  { key: 'text-secondary', path: ['text', 'secondary'], format: 'channels' },
  { key: 'text-tertiary', path: ['text', 'tertiary'], format: 'channels' },
  { key: 'success', path: ['success'], format: 'channels' },
  { key: 'warn', path: ['warn'], format: 'channels' },
  { key: 'error', path: ['error'], format: 'channels' },
  { key: 'link', path: ['link'], format: 'channels' },
  { key: 'bg', path: ['bg', 'DEFAULT'], format: 'channels' },
  { key: 'bg-dark', path: ['bg', 'dark'], format: 'color' },
  { key: 'neutral', path: ['neutral'], format: 'channels' }
] as const satisfies readonly IThemeColorToken[];

export const THEME_RADIUS_KEYS = ['sm', 'md', 'lg', 'xl', '2xl', '3xl'] as const satisfies readonly ThemeRadiusKeyType[];

export const THEME_FONT_STACKS: Record<ThemeFontFamilyType, string> = {
  system:
    "system-ui, -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif",
  pingfang: "'PingFang SC', 'Hiragino Sans GB', system-ui, sans-serif",
  alibaba: "'Alibaba', 'PingFang SC', system-ui, sans-serif",
  'source-han-sans': "'SourceHanSansCN', 'Noto Sans SC', 'PingFang SC', system-ui, sans-serif",
  'source-han-serif': "'SourceHanSerifCN', 'Noto Sans SC', 'PingFang SC', system-ui, sans-serif"
};
export const THEME_FONT_KEYS = Object.keys(THEME_FONT_STACKS) as ThemeFontFamilyType[];

/** 设计稿基准：375 宽 / 37.5 = 1rem */
export const THEME_REM_BASE = 37.5;
/** 圆角上限（设计稿 px） */
export const THEME_RADIUS_MAX_PX = 1000;

/** 跨文档协议与 DOM 标记：变更会破坏 main / community 的独立发布，勿改 */
export const THEME_PROTOCOL = 'h5-host-theme';
export const THEME_PROTOCOL_VERSION = 2;
export const THEME_ACTIVE_ATTR = 'data-h5-host-theme';

const PREFIX = '--h5-theme';
export const themeColorVarName = (key: string, format: IThemeColorToken['format']) =>
  format === 'channels' ? `${PREFIX}-${key}-rgb` : `${PREFIX}-${key}`;
export const themeRadiusVarName = (key: ThemeRadiusKeyType) => `${PREFIX}-radius-${key}`;
export const THEME_FONT_VAR_NAME = `${PREFIX}-font-family`;

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** 读取嵌套路径；不存在时返回 undefined */
export function getByPath(source: unknown, path: readonly string[]): unknown {
  return path.reduce<unknown>((node, key) => (isRecord(node) ? node[key] : undefined), source);
}

/** 按嵌套路径写入；用于把扁平遍历结果还原成 colors 结构 */
export function setByPath(target: Record<string, unknown>, path: readonly string[], value: unknown): void {
  const parent = path.slice(0, -1).reduce<Record<string, unknown>>((node, key) => {
    if (!isRecord(node[key])) node[key] = {};
    return node[key] as Record<string, unknown>;
  }, target);
  parent[path[path.length - 1]] = value;
}
